import {
  useCallback,
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import type { TextInputWithGeolocationProps } from '~/_shared/components/textInputWithGeolocation/textInputWithGeolocation.compontent';
import type { GeoLocation } from '~/_shared/utils/geolocation/geolocation';
import { useGetLocation } from '~/_shared/utils/geolocation/useGetLocation';
import {
  startSelectLocationTool, stopSelectLocationTool,
} from '~/store/frontendState/mapTools/selectLocation/selectLocation.actionCreators';
import {
  useIsSelectLocationActive, useSelectLocationIsLoadingSelector,
} from '~/store/frontendState/mapTools/selectLocation/selectLocation.selectors';
import type { SelectLocationConfiguration } from '~/store/frontendState/mapTools/selectLocation/selectLocation.state';

export const useGeolocationInputProps = ({
  configuration,
  startingLocationState,
  setStartingLocationState,
}: {
  configuration: SelectLocationConfiguration;
  startingLocationState: GeoLocation | null;
  setStartingLocationState: (startingLocation: GeoLocation) => void;
}) => {
  const dispatch = useDispatch();
  const isSelectStartLocationActive = useIsSelectLocationActive(configuration);
  const isSelectLocationLoading = useSelectLocationIsLoadingSelector();

  const [address, setAddress] = useState('');
  const [wasKeyDown, setWasKeyDown] = useState(false);

  const {
    getUserPosition,
    isFetchingGeoLocation,
    isGeoError,
    resetGeoError,
    getLatLngFromAddress,
  } = useGetLocation();

  const onAddressInputChange = useCallback((address: string) => {
    setAddress(address);
    setWasKeyDown(true);
  }, []);

  const onSelectLocationOnMap = useCallback(() => {
    if (isSelectStartLocationActive) {
      dispatch(stopSelectLocationTool());
    }
    else {
      dispatch(startSelectLocationTool(configuration));
    }
  }, [configuration, dispatch, isSelectStartLocationActive]);

  const onSetStartingLocation = useCallback((startingLocation: GeoLocation) => {
    setStartingLocationState(startingLocation);
    setWasKeyDown(false);
  }, [setStartingLocationState]);

  const onGetUserLocalizationClick = useCallback(async () => {
    const response = await getUserPosition();
    setAddress(response.address);
    onSetStartingLocation(response);
  }, [getUserPosition, onSetStartingLocation]);

  const getLatLngFromAddressWrapped = useCallback(async ({
    address,
  }: {
    address: string;
  }) => {
    const response = await getLatLngFromAddress({ address });
    setAddress(response.address);
    onSetStartingLocation(response);
    return response;
  }, [getLatLngFromAddress, onSetStartingLocation]);

  const geolocationInputProps: TextInputWithGeolocationProps = useMemo(() => ({
    value: address,
    hasError: isGeoError,
    isLoading: isFetchingGeoLocation || !!isSelectLocationLoading,
    isSelectLocationActive: isSelectStartLocationActive,
    onChange: onAddressInputChange,
    onGetUserLocation: onGetUserLocalizationClick,
    onSelectLocationOnMap,
  }), [address, isFetchingGeoLocation, isGeoError, isSelectStartLocationActive, isSelectLocationLoading, onAddressInputChange, onGetUserLocalizationClick, onSelectLocationOnMap]);

  useEffect(() => {
    if (startingLocationState?.address) {
      setAddress(startingLocationState.address);
    }
  }, [startingLocationState]);

  const resetGeoErrorRef = useRef(resetGeoError);
  resetGeoErrorRef.current = resetGeoError;

  useEffect(() => {
    resetGeoErrorRef.current();
  }, [startingLocationState]);

  return {
    address,
    wasAddressTyped: wasKeyDown,
    geolocationInputProps,
    getLatLngFromAddress: getLatLngFromAddressWrapped,
  };
};
